import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "card text-center border-0 shadow" }
const _hoisted_2 = {
  key: 0,
  class: "custom-card-header card-header card-img-top"
}
const _hoisted_3 = { class: "custom-card-body card-body rounded" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 1,
  class: "custom-card-footer card-img-bottom"
}

import { ref, computed, provide, watch } from "vue";
import Logos from "./Logos.vue";
import CountdownTimer from "./CountdownTimer.vue";
import Image from "./Image.vue";
import DisplayFields from "./DisplayFields.vue";
import Inputs from "./Inputs.vue";
import ButtonsComponent from "./ButtonsComponent.vue";
import FooterLogos from "./FooterLogos.vue";

import {
  button,
  theme,
  uiContentJSON,
} from "@entersekt/types/build/3-5-0/endpoint";
import { InputAnswer } from "@/models/template";

const black = "#2c3e50";

export default /*@__PURE__*/_defineComponent({
  __name: 'CardTemplate',
  props: {
    template: {},
    templateTheme: {},
    isSilentAuth: { type: Boolean }
  },
  emits: ["buttonClicked"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "77c884a2": (headerBackgroundColor.value),
  "e108508c": (bodyBackgroundColor.value),
  "3fabace1": (footerBackgroundColor.value)
}))

const props = __props;

const headerBackgroundColor = ref(props.templateTheme?.headerBackgroundColor);
const bodyBackgroundColor = ref(props.templateTheme?.bodyBackgroundColor);
const footerBackgroundColor = ref(props.templateTheme?.footerBackgroundColor);

const emit = __emit;

const inputData = ref(new Map<string, number | string>());
const disabled = ref(false);

watch(
  () => props.template,
  () => {
    disabled.value = false;
  },
  { deep: true },
);

const boldRegex = /\*\*(.*?)\*\*/gs;
const italicRegex = /\*(.*?)\*/gs;
const timerRegex = /\(((\d+))\)/g;
const externalLinkRegex = /\[(.*?)\]\((.*?)\)/g;

provide("formatTextFunction", formatText);
const setTitleColor = computed((): string => {
  if (props.templateTheme?.titleTextColor) {
    return props.templateTheme?.titleTextColor;
  }
  return black;
});

const setOverviewColor = computed((): string => {
  return props.templateTheme?.overviewTextColor
    ? props.templateTheme.overviewTextColor
    : black;
});
const setConclusionColor = computed((): string => {
  return props.templateTheme?.conclusionTextColor
    ? props.templateTheme?.conclusionTextColor
    : black;
});

function inputsUpdated(inputs: Map<string, string | number>) {
  inputData.value = inputs;
}

function isJavascriptCountDown(): boolean {
  return props.template.imageURI?.includes("javascript:countdownTimer")
    ? true
    : false;
}

function extractTime(): number | undefined {
  const matchRegex = props.template.imageURI?.match(timerRegex);
  if (matchRegex && matchRegex?.length > 0) {
    return +matchRegex[0].slice(1, matchRegex[0].length - 1);
  }
}

// uses regex to find and replace text wrapped with '**' to first make text bold,
// then finds text wrapped with '*' to italicizes the text.
function emphasisText(text: string | undefined): string | undefined {
  if (!text) {
    return text;
  }
  const boldText = text.replace(boldRegex, "<strong>$1</strong>");
  return boldText.replace(italicRegex, "<em>$1</em>");
}

// Find matching string [text](URL) and converts it to an HTML clickable link
function replaceLink(text: string | undefined): string | undefined {
  if (!text) {
    return text;
  }
  return text.replace(externalLinkRegex, `<a href="$2" target="_blank">$1</a>`);
}

function formatText(text: string | undefined): string | undefined {
  let replacedText = replaceLink(text);
  return emphasisText(replacedText);
}

function buttonClicked(action: button.action, actionNext?: button.actionNext) {
  const form = document.getElementById("inputs-form") as HTMLFormElement;
  if (form && !form.checkValidity() && action === "CONTINUE") {
    form.classList.add("was-validated");
  } else {
    disabled.value = true;
    const inputsArray: InputAnswer[] = [];
    inputData.value.forEach((value, key) => {
      inputsArray.push({ inputID: key, value });
    });
    emit("buttonClicked", action, inputsArray, actionNext);
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    (_ctx.template.headerLogoURIs)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createVNode(Logos, {
            logos: _ctx.template.headerLogoURIs
          }, null, 8, ["logos"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_3, [
      _createElementVNode("h3", {
        class: "textFormat card-title",
        style: _normalizeStyle({ color: setTitleColor.value }),
        innerHTML: formatText(_ctx.template.title)
      }, null, 12, _hoisted_4),
      (isJavascriptCountDown())
        ? (_openBlock(), _createBlock(CountdownTimer, {
            key: 0,
            time: extractTime()
          }, null, 8, ["time"]))
        : (_ctx.template.imageURI)
          ? (_openBlock(), _createBlock(Image, {
              key: 1,
              uri: _ctx.template.imageURI
            }, null, 8, ["uri"]))
          : _createCommentVNode("", true),
      _createElementVNode("h5", {
        class: "textFormat card-text",
        style: _normalizeStyle({ color: setOverviewColor.value }),
        innerHTML: formatText(_ctx.template.overview)
      }, null, 12, _hoisted_5),
      (_ctx.template.displayFields)
        ? (_openBlock(), _createBlock(DisplayFields, {
            key: 2,
            class: "card-text",
            displayFields: _ctx.template.displayFields,
            theme: _ctx.templateTheme
          }, null, 8, ["displayFields", "theme"]))
        : _createCommentVNode("", true),
      (_ctx.template.inputFields)
        ? (_openBlock(), _createBlock(Inputs, {
            key: 3,
            onInputUpdated: inputsUpdated,
            inputs: _ctx.template.inputFields,
            answers: inputData.value,
            "is-Disabled": disabled.value
          }, null, 8, ["inputs", "answers", "is-Disabled"]))
        : _createCommentVNode("", true),
      _createElementVNode("p", {
        class: "textFormat card-text",
        style: _normalizeStyle({ color: setConclusionColor.value }),
        innerHTML: formatText(_ctx.template.conclusion)
      }, null, 12, _hoisted_6),
      (_ctx.template.buttons && !_ctx.isSilentAuth)
        ? (_openBlock(), _createBlock(ButtonsComponent, {
            key: 4,
            onButtonClicked: buttonClicked,
            buttons: _ctx.template.buttons,
            theme: _ctx.templateTheme,
            isDisabled: disabled.value
          }, null, 8, ["buttons", "theme", "isDisabled"]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.template.footerLogoURIs)
      ? (_openBlock(), _createElementBlock("section", _hoisted_7, [
          _createVNode(FooterLogos, {
            logos: _ctx.template.footerLogoURIs
          }, null, 8, ["logos"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})